import * as React from 'react';
import { compose, withHandlers, withProps, withState } from 'recompose';
import { connect } from 'react-redux';
import * as moment from 'moment';
import { skin, OemProps } from 'ui/styles/skin';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { State } from 'reducers';
import { AlarmId, AlarmMap, DeviceMap, PolicyMap, UtcTime, DeviceIdMap } from 'types';
import { Column, Index, SortDirection } from 'react-virtualized';
import { Table } from 'ui/shared/Table';
import { waitUntilLoaded } from 'ui/shared/waitUntilLoaded';
import { getAlarmMap, getAlarmIdSequence, isLoadingAlarms } from 'selectors/alarmSelectors';
import { getDevices, getDeviceIdByDeviceIndex, isAccessDevice } from 'selectors/deviceSelectors';
import { getPolicyMap, isLoadingPolicies, getPoliciesError, getPolicyTranslatedName } from 'selectors/policySelectors';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { displayAlarm } from 'actions/alarmActions';

import './AlarmListView.css';

const styles = {
    halfOpacity: {
        opacity: .5,
    },
};

const EmptyListView = ({ t }: TranslateProps) =>
    <div className="no-alarms">
        { t('6.1241.0.90') }
    </div>
;

const Deleted = ({ t }: TranslateProps) =>
    <span style={ styles.halfOpacity }>
        { t('6.2243.11') }
    </span>
;

// We're only passing Alarm IDs to the table as input... so we need a lookup function to
// return the cell contents based on an alarm ID and a column name. This is used for
// sorting the columns.
const makeCellGetter = (alarmMap: AlarmMap, policyMap: PolicyMap, deviceMap: DeviceMap, deviceIdByDeviceIndex: DeviceIdMap, { t }: TranslateProps) =>
    (alarmId: AlarmId, column: string | null) => {
        const alarm = alarmMap[alarmId];
        if (!alarm) return '';

        switch (column || 'time') {
            case 'time':
                return alarm.time;

            case 'deviceId':
                const device = deviceMap[alarm.deviceId];
                return device && device.name || '';

            case 'policyId':
                const policy = policyMap[alarm.policyId];
                return policy && getPolicyTranslatedName(policy, { t }) || '';
            
            default:
                throw new Error(`Unexpected column: ${column}`);
        }
    }
;

export const RawAlarmListView = ({
    alarmIds,
    alarmMap,
    handleClickAlarm,
    policyMap,
    deviceMap,
    deviceIdByDeviceIndex,
    tzOffsetCalculator,
    rowStyle,
    t,
}: Props) =>
    <div id="alarm-list" className="abs">
        <Table
            items={ alarmIds }
            noRowsRenderer={ () => <EmptyListView t={ t }/> }
            cellGetter={ makeCellGetter(alarmMap, policyMap, deviceMap, deviceIdByDeviceIndex, { t }) }
            rowStyle={ rowStyle }
            onClickRowItem={ handleClickAlarm }
            initialSortBy="time"
            initialSortDirection={ SortDirection.DESC }
        >
            <Column
                dataKey="time"
                width={ 120 }
                label={ t('405') }
                disableSort={ true }
                cellRenderer={
                    ({ rowData: alarmId }) => {
                        const alarm = alarmMap[alarmId];
                        if (!alarm) {
                            return '';
                        }
                        const { time } = alarm;
                        return moment
                            .utc(time)
                            .utcOffset(tzOffsetCalculator(time))
                            .format('LTS')
                        ;
                    }
                }
            />
            <Column
                dataKey="deviceId"
                width={ 200 }
                label={ t('184') }
                disableSort={ true }
                cellRenderer={
                    ({ rowData: alarmId }) => {
                        const alarm = alarmMap[alarmId];
                        if (!alarm) {
                            return '';
                        }
                        // Alarm contains "deviceId"... but it's really the device *index* as a string
                        const { policyId, deviceId: deviceIndexString } = alarm;
                        let deviceId = deviceIdByDeviceIndex[deviceIndexString];
                        let device = deviceMap[deviceId];
                        if (isAccessDevice(device)) {
                            const policy = policyMap[policyId];
                            if (policy) {
                                // Look up the camera attached to the rule
                                try {
                                    deviceId = policy.actions.alarm.cameraId;
                                    device = deviceMap[deviceId];
                                }
                                catch (err) {}
                            }
                        }
                        return device
                            ? device.name
                            : <Deleted t={ t }/>
                        ;
                    }
                }
                flexGrow={ 1 }
            />
            <Column
                dataKey="policyId"
                width={ 200 }
                label={ t('907') }
                disableSort={ true }
                cellRenderer={
                    ({ rowData: alarmId }) => {
                        const alarm = alarmMap[alarmId];
                        if (!alarm) {
                            return '';
                        }
                        const { policyId } = alarm;
                        const policy = policyMap[policyId];
                        return policy
                            ? getPolicyTranslatedName(policy, { t })
                            : <Deleted t={ t }/>
                        ;
                    }
                }
                flexGrow={ 1 }
            />
        </Table>
    </div>
;

type ContainerProps = {
    height: number;
};
type StateProps = {
    alarmIds: AlarmId[];
    alarmMap: AlarmMap;
    loading: boolean;
    deviceMap: DeviceMap;
    deviceIdByDeviceIndex: DeviceIdMap;
    policyMap: PolicyMap;
    loadingPolicies: boolean;
    policiesError: Error | null;
    tzOffsetCalculator: (t: UtcTime) => number;
};
type DispProps = {
    goToAlarm: (alarmId: AlarmId) => void;
};
type RecomposeProps = {
    selectedAlarmId: AlarmId | null;
    handleClickAlarm: (alarmId: AlarmId) => void;
    rowStyle: (info: Index) => React.CSSProperties;
};

const mapStateToProps = (state: State): StateProps => ({
    alarmMap: getAlarmMap(state),
    alarmIds: getAlarmIdSequence(state),
    loading: isLoadingAlarms(state),
    deviceMap: getDevices(state) || {},
    deviceIdByDeviceIndex: getDeviceIdByDeviceIndex(state),
    policyMap: getPolicyMap(state),
    loadingPolicies: isLoadingPolicies(state),
    policiesError: getPoliciesError(state),
    tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

const mapDispatchToProps = (dispatch: Function): DispProps => ({
    goToAlarm: (alarmId: AlarmId) => dispatch(displayAlarm(alarmId)),
});

type Props = ContainerProps & StateProps & RecomposeProps & OemProps & TranslateProps;

export const AlarmListView = compose<Props, {}>(
    skin,
    translate(),
    connect(mapStateToProps, mapDispatchToProps),
    waitUntilLoaded({ size: 'small' }),
    withState('selectedAlarmId', '_setSelectedAlarmId', null),
    withHandlers({
        handleClickAlarm: ({ goToAlarm, _setSelectedAlarmId }) => (alarmId: AlarmId) => {
            _setSelectedAlarmId(alarmId);
            goToAlarm(alarmId);
        },
    }),
    withProps<Partial<Props>, Props>(({ selectedAlarmId, alarmIds, styles, handleClickAlarm }) => ({
        rowStyle: ({ index, item }: Index & { item: AlarmId }) => {
            return selectedAlarmId && item === selectedAlarmId
                ? styles.TR_Selected
                : styles.TR;
        },
    })),
)(RawAlarmListView);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/sidePanel/AlarmListView.tsx